<mat-card>
  <mat-card-title>My Profile</mat-card-title>
  <mat-card-content>
    @let profileFormGroup = formGroup | select:emptyFormGroup;
    @let loaded = currentPatronSelectors.finishedLoading | select:false;
    @let hasGoogleUid = currentPatronSelectors.hasGoogleUid | select:false;
    @let isMdOrAbove = viewportSelectors.isMdAndUp | select;
    @let isSm = viewportSelectors.isSm | select;

    <div mxHiddenUntilLoaded [reveal]="loaded" class="flex flex-col md:flex-row gap-10">
      <!-- Main Profile Form -->
      <div class="@container/form relative order-2 md:order-1 flex flex-col md:gap-4 basis-full md:basis-2/3 shrink grow-0">
        <!-- Name & Date of Birth -->
        <div class="flex flex-col mb-4">
          <p mxTruncate [mxTruncateMaxLines]="2" class="text-title-large">{{ currentPatronSelectors.fullName | select }}</p>
          <p class="text-body-large text-on-surface-variant"><b>DOB: </b>{{ currentPatronSelectors.dateOfBirth | select | format:'DATE_ONLY' }}</p>
        </div>

        <!-- Preferred Name & Pronouns -->
        <mx-form-row class="gap-3">
          <mx-input-form-field fieldName="preferredName" [formGroup]="profileFormGroup" class="w-1/2"/>
          <mx-select-form-field fieldName="pronounId" overrideLabel="Pronouns" [formGroup]="profileFormGroup" class="w-1/2"
                                [options]="referenceOptionSelectors.pronounSelectOptions | select:[]" [includeUndefinedOption]="true"/>
        </mx-form-row>

        <!-- Addresses -->
        <div class="w-full grid grid-cols-1 @[25.5rem]/form:grid-cols-2 gap-3 mb-5">
          @for (address of currentPatronSelectors.addresses | select; track address.id) {
            <px-patron-address-display [address]="address" [isPrimary]="$first"/>
          }
        </div>

        <!-- Pickup Branch -->
        <mx-select-form-field fieldName="defaultHoldPickupBranchId" overrideLabel="Pickup Location" [formGroup]="profileFormGroup" class="w-full"
                              [options]="referenceOptionSelectors.branchSelectOptions | select:[]" [includeUndefinedOption]="false"/>

        <!-- Phone Info -->
        <ng-container *ngTemplateOutlet="phoneInputSection; context: {
                          phoneFieldName: 'phone1',
                          phoneFieldLabel: 'Primary Phone',
                          extFieldName: 'phone1Ext',
                          cbxFieldName: 'phone1Mobile'
                        }"/>
        <ng-container *ngTemplateOutlet="phoneInputSection; context: {
                          phoneFieldName: 'phone2',
                          phoneFieldLabel: 'Secondary Phone',
                          extFieldName: 'phone2Ext',
                          cbxFieldName: 'phone2Mobile'
                        }"/>

        <mx-filled-button label="Save Changes" [enabled]="hasChanged(profileFormGroup) && profileFormGroup.valid" (click)="onClickSaveButton(profileFormGroup)"
                          class="self-end"/>

        <ng-template #phoneInputSection
                     let-phoneFieldName="phoneFieldName"
                     let-phoneFieldLabel="phoneFieldLabel"
                     let-extFieldName="extFieldName"
                     let-cbxFieldName="cbxFieldName">
          <mx-form-row class="gap-0">
            <mx-input-form-field [fieldName]="phoneFieldName" [formGroup]="profileFormGroup" class="w-[65%] md:w-[50%] md:me-4"
                                 [overrideLabel]="isMdOrAbove ? `${phoneFieldLabel} Number` : phoneFieldLabel"/>
            @if (isMdOrAbove) {
              <ng-container *ngTemplateOutlet="phoneExtension; context: {fieldName: extFieldName}"/>
            }
            <mx-checkbox-form-field [fieldName]="cbxFieldName" overrideLabel="Mobile" [formGroup]="profileFormGroup" class="w-[35%] md:w-[25%]"/>
          </mx-form-row>
          @if (isSm) {
            <ng-container *ngTemplateOutlet="phoneExtension; context: {fieldName: extFieldName}"/>
          }
        </ng-template>

        <ng-template #phoneExtension let-fieldName="fieldName">
          <mx-input-form-field [fieldName]="fieldName" overrideLabel="Ext." [formGroup]="profileFormGroup" class="w-[25%] md:w-[25%] min-w-[5.65rem]"/>
        </ng-template>
      </div>

      <!-- Library Card + Change Data Triggers Column -->
      <div class="order-1 md:order-2 flex flex-col gap-5 basis-full md:basis-1/3 grow">
        <!-- Library Card -->
        <px-library-card class="self-center md:self-start"/>

        <!-- Change Important Data Dialog Triggers -->
        <div class="flex flex-col gap-y-3 w-full">
          @let responsiveAccountSectionCss = 'flex flex-row md:flex-col items-center md:items-stretch';
          @let accountDetailCss = 'flex flex-col gap-y-1';
          @let responsiveWidthCss = 'w-1/2 md:w-full';
          @let responsiveAccountDetailCss = `${accountDetailCss} ${responsiveWidthCss}`;

          @if (hasGoogleUid) {
            @let hasPendingEmailChange = currentPatronSelectors.hasPendingEmailChange | select:false;

            <!-- Email -->
            <div class="flex flex-col">
              <div [class]="accountDetailCss">
                <div class="flex flex-row flex-wrap justify-between items-baseline gap-x-2">
                  <p class="text-title-large">Email</p>

                  @if (hasPendingEmailChange) {
                    <p class="text-body-large text-outline">
                      {{ (currentPatronSelectors.pendingEmailChangeHasExpired | select) ? '*verification expired' : '*pending verification' }}
                    </p>
                  }
                </div>

                <p class="text-body-large break-all">
                  @if (hasPendingEmailChange) {
                    <span class="text-outline">{{ currentPatronSelectors.pendingEmail | select }}</span>
                  } @else {
                    {{ currentPatronSelectors.email | select }}
                  }
                </p>
              </div>

              <mx-action-buttons>
                @if (hasPendingEmailChange) {
                  <mx-text-button label="Resend Verification Email" class="me-auto" [enabled]="!recentlyResentEmailVerification"
                                  disabledTooltip="Please wait a moment to resend the email." (click)="onClickResendEmailVerification()"/>
                }
                <mx-text-button label="Change Email" (click)="onClickChangeEmail()"/>
              </mx-action-buttons>
            </div>

            <!-- Password -->
            <div [class]="responsiveAccountSectionCss">
              <div [class]="responsiveAccountDetailCss">
                <p class="text-title-large">Password</p>
                <p class="text-body-large">**********</p>
              </div>

              <mx-action-buttons [class]="responsiveWidthCss">
                <mx-text-button label="Change Password" (click)="onClickChangePassword()"/>
              </mx-action-buttons>
            </div>
          }

          <!-- PIN -->
          <div [class]="responsiveAccountSectionCss">
            <div [class]="responsiveAccountDetailCss">
              <p class="text-title-large">PIN</p>
              <p class="text-body-large">****</p>
            </div>

            <mx-action-buttons [class]="responsiveWidthCss">
              <mx-text-button label="Change PIN" (click)="onClickChangePIN()"/>
            </mx-action-buttons>
          </div>
        </div>
      </div>
    </div>
  </mat-card-content>
</mat-card>
